import type { EaseTypeImpl } from '../types';
import { sqr } from '@use-gpu/core';
import { for2 } from './zip';
import { vec3, quat, mat4 } from 'gl-matrix';

import { EaseNumber } from './number';
import { EaseQuat } from './quat';

const decomposeMatrix = (m: mat4) => {
  const p = vec3.create();
  const q = quat.create();
  const s = vec3.create();

  mat4.getTranslation(p, m);
  mat4.getRotation(q, m);
  mat4.getScaling(s, m);

  const m2 = mat4.create();
  mat4.fromRotationTranslationScale(m2, q, p, s);
  mat4.invert(m2, m2);
  mat4.multiply(m2, m, m2);

  return {p, q, s, m: m2};
};

const vp = vec3.create();
const vq = quat.create();
const vs = vec3.create();
const vm = mat4.create();

export const EaseMat4: EaseTypeImpl<mat4> = {
  spline: (a: mat4, b: mat4, knots?: mat4[]) => {
    const am = decomposeMatrix(a);
    const bm = decomposeMatrix(b);

    let easeP, easeQ, easeS, easeM;

    if (knots) {
      const [k1, k2] = knots;
      if (!k1 || !k2) throw new Error("Spline ease needs 2 knots");

      const k1m = decomposeMatrix(k1);
      const k2m = decomposeMatrix(k2);

      easeP = EaseNumber.spline(am.p, bm.p, [k1m.p, k2m.p]);
      easeQ = EaseQuat.spline(am.q, bm.q, [k1m.q, k2m.q]);
      easeS = EaseNumber.spline(am.s, bm.s, [k1m.s, k2m.s]);
      easeM = EaseNumber.spline(am.m, bm.m, [k1m.m, k2m.m]);
    }
    else {
      easeP = EaseNumber.spline(am.p, bm.p);
      easeQ = EaseQuat.spline(am.q, bm.q);
      easeS = EaseNumber.spline(am.s, bm.s);
      easeM = EaseNumber.spline(am.m, bm.m);
    }

    return (target: mat4, t: number) => {
      easeP(vp, t);
      easeQ(vq, t);
      easeS(vs, t);
      easeM(vm, t);

      mat4.fromRotationTranslationScale(target, vq, vp, vs);
      mat4.multiply(target, vm, target);
      return target;
    };
  },
  auto: (a: mat4, b: mat4, c: mat4, d: mat4) => {
    const am = decomposeMatrix(a);
    const bm = decomposeMatrix(b);
    const cm = decomposeMatrix(c);
    const dm = decomposeMatrix(d);

    const [p1, p2] = EaseNumber.auto(am.p, bm.p, cm.p, dm.p);
    const [q1, q2] = EaseQuat.auto(am.q, bm.q, cm.q, dm.q);
    const [s1, s2] = EaseNumber.auto(am.s, bm.s, cm.s, dm.s);
    const [m1, m2] = EaseNumber.auto(am.m, bm.m, cm.m, dm.m);

    const k1 = mat4.create();
    const k2 = mat4.create();

    mat4.fromRotationTranslationScale(k1, q1, p1 as vec3, s1 as vec3);
    mat4.multiply(k1, m1 as mat4, k1);

    mat4.fromRotationTranslationScale(k2, q2, p2 as vec3, s2 as vec3);
    mat4.multiply(k2, m2 as mat4, k2);

    return [k1, k2];
  },
  lerp: (target: mat4, a: mat4, b: mat4, t: number) => {
    const am = decomposeMatrix(a);
    const bm = decomposeMatrix(b);

    const easeP = EaseNumber.spline(am.p, bm.p);
    const easeQ = EaseQuat.spline(am.q, bm.q);
    const easeS = EaseNumber.spline(am.s, bm.s);
    const easeM = EaseNumber.spline(am.m, bm.m);

    easeP(vp, t);
    easeQ(vq, t);
    easeS(vs, t);
    easeM(vm, t);

    mat4.fromRotationTranslationScale(target, vq, vp, vs);
    mat4.multiply(target, vm, target);
    return target;
  },
  measure: (a: mat4, b: mat4) => {
    let d = 0;
    for2(a, b, (a: number, b: number) => { d += sqr(a - b); });
    return Math.sqrt(d);
  },
};
